"use client";

import { useRouter } from "next/navigation";
import { DataTable, EmptyState } from "@/components/shared";
import type { PublisherGame } from "../types";
import { publisherGamesColumns } from "../config";
import { PublisherGameCards } from "./PublisherGameCards";
import {
  parsePublisherGamesSortParts,
  togglePublisherGamesSortDirection,
  createPublisherGamesSortForColumn,
  type PublisherGamesSort,
  type PublisherGamesSortColumn,
} from "../utils/publisherGamesSort";

interface PublisherGamesListProps {
  games: PublisherGame[];
  sortBy: PublisherGamesSort;
  onSortChange: (sort: PublisherGamesSort) => void;
}

export function PublisherGamesList({
  games,
  sortBy,
  onSortChange,
}: PublisherGamesListProps) {
  const router = useRouter();

  const handleSortChange = (sortKey: string) => {
    const { column: currentColumn } = parsePublisherGamesSortParts(sortBy);
    if (sortKey === currentColumn) {
      onSortChange(togglePublisherGamesSortDirection(sortBy));
    } else {
      onSortChange(
        createPublisherGamesSortForColumn(sortKey as PublisherGamesSortColumn),
      );
    }
  };

  if (games.length === 0) {
    return <EmptyState message="No games found for this publisher." />;
  }

  const { column: currentSortKey, direction: currentSortOrder } =
    parsePublisherGamesSortParts(sortBy);

  return (
    <div className="bg-[#16202d] rounded-lg border border-[#2a475e] overflow-hidden">
      <DataTable
        data={games}
        columns={publisherGamesColumns}
        getRowHref={(game) => `/app/${game.appid}`}
        getRowAriaLabel={(game) => game.name}
        getRowKey={(game) => game.appid}
        currentSortKey={currentSortKey}
        sortOrder={currentSortOrder}
        onSortChange={handleSortChange}
      />
      <PublisherGameCards
        games={games}
        onCardClick={(appid) => router.push(`/app/${appid}`)}
      />
    </div>
  );
}
